import * as React from "react";
import styled, { css } from "styled-components";
import { IfPermitted, PaginatedList, SearchForm, Spinner } from "components/common";
import { Button, ButtonGroup, Col, Row, Table } from "components/bootstrap";
import QueryHelper from "components/common/QueryHelper";
import ForwarderEntry from "./ForwarderEntry";
import CreateForwarderButton from "./CreateForwarderButton";
import { StyledSearchFormWrapper, StyledToolbar } from "../wizard/StyledWizardComponents";
import { StyledSortIcon } from "../CommonStyledComponents";
const StyledTable = styled(Table)`
  tr > th {
    width: 15%;

    &.rowTitle {
      width: 25%;
    }
    &.rowStatus {
      width: 120px;
      min-width: 120px;
    }
    &.rowDescription {
      width: 20%;
    }
    &.rowVersion {
      width: 150px;
      min-width: 150px;
    }
    &.rowMetrics {
      width: 10%;
    }
    &.rowActions {
      width: 160px;
      min-width: 160px;
    }
  }
`;
const ResponsiveTable = styled.div.attrs(() => ({
  className: "table-responsive"
}))(
  ({ theme }) => css`
    @media (max-width: ${theme.breakpoints.max.md}) {
      .dropdown-menu {
        position: static !important;
      }
    }
    @media (min-width: ${theme.breakpoints.min.md}) {
      overflow: inherit;
    }
  `
);
const StyledSubHeader = styled.span`
  margin-left: 0.5em;
`;
const StyledButtonGroup = styled(ButtonGroup)`
  margin-bottom: 10px;
`;
const ForwardersTable = ({
  forwarders,
  inputProfiles
}) => {
  if (forwarders.length === 0) {
    return /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", { colSpan: 100 }, "No Forwarders match your search criteria."));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, forwarders.map((forwarder) => {
    const inputProfile = inputProfiles.find((i) => i.id === forwarder.input_profile_id);
    return /* @__PURE__ */ React.createElement(ForwarderEntry, { key: forwarder.id, forwarder, inputProfile: inputProfile || null });
  }));
};
const queryExamples = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Find Forwarder by parts of their title:\xA0", /* @__PURE__ */ React.createElement("kbd", null, "title:local"), "\xA0", /* @__PURE__ */ React.createElement("kbd", null, "title:database")), /* @__PURE__ */ React.createElement("p", null, "Searching without a field name matches against the ", /* @__PURE__ */ React.createElement("code", null, "title"), " field: \xA0", /* @__PURE__ */ React.createElement("kbd", null, "local"), " is the same as \xA0", /* @__PURE__ */ React.createElement("kbd", null, "title:local")));
const fieldMap = {
  hostname: "The hostname of the Forwarder",
  version: "The version of the Forwarder"
};
const HelpPopover = () => /* @__PURE__ */ React.createElement(
  QueryHelper,
  {
    entityName: "Forwarder",
    example: queryExamples,
    commonFields: ["title", "description"],
    fieldMap
  }
);
const ForwardersListComponent = ({
  forwarders,
  inputProfiles,
  isLoading = false,
  onQueryChange = () => {
  },
  onSortChange = () => {
  },
  onStateFilterChange = (_filter) => {
  },
  pagination
}) => {
  const { total, query, sortByField, order, stateFilter } = pagination;
  const _handleSortChange = (field) => {
    let nextOrder = "asc";
    if (sortByField === field) {
      nextOrder = order === "asc" ? "desc" : "asc";
    }
    onSortChange(field, nextOrder);
  };
  const _handleStateFilterChange = (name) => {
    onStateFilterChange(name);
  };
  const tableHeaders = [
    { sortable: true, field: "title", label: "Title", className: "rowTitle" },
    { sortable: true, field: "state", label: "Status", className: "rowStatus" },
    { sortable: true, field: "description", label: "Description", className: "rowDescription" },
    { sortable: true, field: "hostname", label: "Hostname", className: "rowHostname" },
    { sortable: true, field: "version", label: "Version", className: "rowVersion" },
    { sortable: false, field: void 0, label: "Input Profile", className: "rowInputProfile" },
    { sortable: false, field: void 0, label: "Metrics", className: "rowMetrics" },
    { sortable: false, field: void 0, label: "Actions", className: "rowActions" }
  ];
  const queryHelp = /* @__PURE__ */ React.createElement(HelpPopover, null);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement("h2", null, "Forwarders", /* @__PURE__ */ React.createElement("small", null, /* @__PURE__ */ React.createElement(StyledSubHeader, null, `${total} Total`), /* @__PURE__ */ React.createElement(StyledSubHeader, null, isLoading && /* @__PURE__ */ React.createElement(Spinner, { text: "" })))), /* @__PURE__ */ React.createElement(StyledSearchFormWrapper, null, /* @__PURE__ */ React.createElement(SearchForm, { query, queryHelpComponent: queryHelp, onSearch: onQueryChange, onReset: onQueryChange }, /* @__PURE__ */ React.createElement(StyledToolbar, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "forwarders:create" }, /* @__PURE__ */ React.createElement(CreateForwarderButton, { buttonTitle: "New Forwarder" }))))), /* @__PURE__ */ React.createElement(StyledButtonGroup, null, /* @__PURE__ */ React.createElement(
    Button,
    {
      active: stateFilter === "connected",
      name: "connected",
      bsSize: "sm",
      onClick: () => _handleStateFilterChange("connected")
    },
    "Connected"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      active: stateFilter === "disconnected",
      name: "disconnected",
      bsSize: "sm",
      onClick: () => _handleStateFilterChange("disconnected")
    },
    "Disconnected"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      active: stateFilter === "any",
      name: "any",
      bsSize: "sm",
      onClick: () => _handleStateFilterChange("any")
    },
    "Any"
  )), /* @__PURE__ */ React.createElement(PaginatedList, { totalItems: total }, /* @__PURE__ */ React.createElement(ResponsiveTable, null, /* @__PURE__ */ React.createElement(StyledTable, { hover: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, tableHeaders.map(
    ({ sortable, field, label, className }) => sortable && field ? /* @__PURE__ */ React.createElement("th", { key: field, className }, label, /* @__PURE__ */ React.createElement(
      StyledSortIcon,
      {
        onChange: () => _handleSortChange(field),
        activeDirection: sortByField === field ? order : null,
        ascId: "asc",
        descId: "desc"
      }
    )) : /* @__PURE__ */ React.createElement("th", { key: label, className }, label)
  ))), /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement(ForwardersTable, { forwarders, inputProfiles }))))))));
};
export default ForwardersListComponent;
